/* Nordri page sections: start each section's motion when it comes into view (assets/nordri-pages.css);
   the help search in the page hero (questions suggested while typing, from the hero's "Search
   suggestion" blocks and, when switched on, the FAQ questions on the same page); and the gallery,
   engraving preview, tabs, gift finder and journal search. */
(() => {
  if (window.__nordriPages) return;
  window.__nordriPages = true;

  const show = (el) => el.classList.add('is-in');
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) {
        show(e.target);
        io.unobserve(e.target);
      }
    }), { threshold: 0.15 })
    : null;

  const watch = (root) => root.querySelectorAll('.np--motion:not(.is-in)').forEach((el) => (io ? io.observe(el) : show(el)));

  /* ---------- help search ---------- */
  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  // "shipping" also finds "shipped" and "ships": long words are matched by their first letters
  const stem = (w) => (w.length > 4 ? w.slice(0, Math.max(4, w.length - 4)) : w);

  function setupSuggest(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = '1';
    const input = form.querySelector('input');
    const box = form.querySelector('.np-suggest');
    if (!input || !box) return;
    let cfg = { items: [] };
    try {
      cfg = JSON.parse(form.querySelector('[data-np-suggest-data]').textContent);
    } catch (e) {
      /* no suggestions configured */
    }
    const items = (cfg.items || []).filter((x) => x && x.q);
    if (form.hasAttribute('data-page-faq')) {
      document.querySelectorAll('.np-faq__item').forEach((d) => {
        const q = d.querySelector('summary')?.textContent.trim();
        if (!q || items.some((x) => norm(x.q) === norm(q))) return;
        const a = (d.querySelector('.np-faq__answer')?.textContent || '').trim();
        items.push({ q, a: a.length > 110 ? `${a.slice(0, 108)}…` : a, k: '', t: '', u: `#${d.id}`, el: d });
      });
    }

    let shown = [];
    let active = -1;

    const score = (it, stems) => {
      const q = norm(it.q);
      const hay = `${q} ${norm(it.k)} ${norm(it.t)} ${norm(it.a)}`;
      let s = 0;
      for (const w of stems) {
        if (!hay.includes(w)) return -1;
        s += q.includes(w) ? 3 : 0;
        s += norm(it.k).includes(w) ? 2 : 1;
      }
      return s;
    };

    const mark = (text, stems) => {
      let out = esc(text);
      stems.forEach((w) => {
        if (w.length < 2) return;
        out = out.replace(new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'), '<mark>$1</mark>');
      });
      return out;
    };

    const render = () => {
      const words = norm(input.value).split(/[^a-z0-9]+/).filter(Boolean);
      const stems = words.map(stem);
      let html = '';
      if (!words.length) {
        shown = items.slice(0, 5);
        if (shown.length) html += '<div class="np-suggest__head">Popular questions</div>';
      } else {
        shown = items
          .map((it) => ({ it, s: score(it, stems) }))
          .filter((x) => x.s >= 0)
          .sort((a, b) => b.s - a.s)
          .slice(0, 8)
          .map((x) => x.it);
      }
      active = -1;
      html += shown
        .map((it, i) => `<a class="np-suggest__item" role="option" id="${box.id}-${i}" data-i="${i}" href="${esc(it.u || '#')}" aria-selected="false">
          <span class="np-suggest__q"><span>${mark(it.q, stems)}</span>${it.t ? `<span class="np-suggest__t">${esc(it.t)}</span>` : ''}</span>
          ${it.a ? `<span class="np-suggest__a">${esc(it.a)}</span>` : ''}
        </a>`)
        .join('');
      if (words.length && !shown.length) {
        html = `<div class="np-suggest__empty">${esc(cfg.empty || 'No answer for that yet.')} ${cfg.emptyLabel ? `<a href="${esc(cfg.emptyLink || '/pages/contact')}">${esc(cfg.emptyLabel)} →</a>` : ''}</div>`;
      }
      box.innerHTML = html;
      box.hidden = !html;
      input.setAttribute('aria-expanded', String(!box.hidden));
    };

    const close = () => {
      box.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    };

    const choose = (i) => {
      const it = shown[i];
      if (!it) return;
      close();
      if (it.el) {
        it.el.open = true;
        it.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        it.el.classList.add('is-flash');
        setTimeout(() => it.el.classList.remove('is-flash'), 1600);
        return;
      }
      if (it.u) {
        const target = it.u.startsWith('#') ? document.querySelector(it.u) : null;
        if (target) {
          if (target.tagName === 'DETAILS') target.open = true;
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.location.href = it.u;
        }
      }
    };

    const highlight = (i) => {
      const opts = [...box.querySelectorAll('.np-suggest__item')];
      if (!opts.length) return;
      active = (i + opts.length) % opts.length;
      opts.forEach((o, j) => o.setAttribute('aria-selected', String(j === active)));
      input.setAttribute('aria-activedescendant', opts[active].id);
      opts[active].scrollIntoView({ block: 'nearest' });
    };

    input.addEventListener('input', render);
    input.addEventListener('focus', render);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (box.hidden) render();
        highlight(active + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(active - 1);
      } else if (e.key === 'Escape') {
        close();
      }
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (box.hidden) render();
      choose(active >= 0 ? active : 0);
    });
    box.addEventListener('click', (e) => {
      const a = e.target.closest('.np-suggest__item');
      if (!a) return;
      e.preventDefault();
      choose(Number(a.dataset.i));
    });
    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) close();
    });
  }

  const once = (el) => {
    if (el.dataset.npReady) return false;
    el.dataset.npReady = '1';
    return true;
  };
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pick = (chips, on) => chips.forEach((c) => {
    c.classList.toggle('is-on', c === on);
    if (c.hasAttribute('aria-checked')) c.setAttribute('aria-checked', String(c === on));
  });

  /* ---------- gallery: filters and a full-screen view with swipe ---------- */
  const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  function setupGallery(gal) {
    if (!once(gal)) return;
    const section = gal.closest('.np');
    const filter = section?.querySelector('[data-np-gal-filter]');
    filter?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cat]');
      if (!b) return;
      pick([...filter.querySelectorAll('[data-cat]')], b);
      gal.querySelectorAll('.np-gal__item').forEach((it) => (it.hidden = !!b.dataset.cat && it.dataset.cat !== b.dataset.cat));
    });
    gal.addEventListener('click', (e) => {
      const open = e.target.closest('.np-gal__open');
      if (!open) return;
      const list = [...gal.querySelectorAll('.np-gal__item:not([hidden]) .np-gal__open')];
      let i = list.indexOf(open);
      const box = document.createElement('div');
      box.className = 'np-lb';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.innerHTML = `<img alt=""><button class="np-lb__close np-lightbox__close" type="button" aria-label="Close">${ICON_X}</button>${list.length > 1 ? '<button class="np-lb__prev np-lightbox__prev" type="button" aria-label="Previous photo">‹</button><button class="np-lb__next np-lightbox__next" type="button" aria-label="Next photo">›</button>' : ''}`;
      const img = box.querySelector('img');
      const paint = (d = 0) => {
        img.src = list[i].dataset.full;
        img.alt = list[i].querySelector('img')?.alt || '';
        if (d && !reduced()) img.animate([{ opacity: 0.2, transform: `translateX(${d * 60}px)` }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      };
      const go = (d) => {
        if (list.length < 2) return;
        i = (i + d + list.length) % list.length;
        paint(d);
      };
      const close = () => {
        box.remove();
        document.removeEventListener('keydown', key);
        open.focus();
      };
      const key = (ev) => {
        if (ev.key === 'Escape') close();
        if (ev.key === 'ArrowRight') go(1);
        if (ev.key === 'ArrowLeft') go(-1);
      };
      let sx = null;
      let sy = 0;
      let swiped = false;
      box.addEventListener('pointerdown', (ev) => {
        sx = ev.clientX;
        sy = ev.clientY;
        swiped = false;
      });
      box.addEventListener('pointerup', (ev) => {
        if (sx === null) return;
        const d = ev.clientX - sx;
        if (Math.abs(d) > 50 && Math.abs(d) > Math.abs(ev.clientY - sy)) {
          swiped = true;
          go(d < 0 ? 1 : -1);
        }
        sx = null;
      });
      let acc = 0;
      let lock = 0;
      box.addEventListener('wheel', (ev) => {
        if (Math.abs(ev.deltaX) <= Math.abs(ev.deltaY)) return;
        ev.preventDefault();
        if (performance.now() < lock) return;
        acc += ev.deltaX;
        if (Math.abs(acc) > 50) {
          go(acc > 0 ? 1 : -1);
          acc = 0;
          lock = performance.now() + 450;
        }
      }, { passive: false });
      box.addEventListener('click', (ev) => {
        if (swiped) {
          swiped = false;
          return;
        }
        if (ev.target === box || ev.target.closest('.np-lb__close')) close();
        else if (ev.target.closest('.np-lb__prev')) go(-1);
        else if (ev.target.closest('.np-lb__next')) go(1);
      });
      document.addEventListener('keydown', key);
      document.body.append(box);
      paint();
      box.querySelector('.np-lb__close').focus();
    });
  }

  /* ---------- engraving preview ---------- */
  const RUNES = {
    A: 'ᚨ', B: 'ᛒ', C: 'ᚲ', K: 'ᚲ', Q: 'ᚲ', D: 'ᛞ', E: 'ᛖ', F: 'ᚠ', G: 'ᚷ', H: 'ᚺ', I: 'ᛁ',
    J: 'ᛃ', Y: 'ᛃ', L: 'ᛚ', M: 'ᛗ', N: 'ᚾ', O: 'ᛟ', P: 'ᛈ', R: 'ᚱ', S: 'ᛊ', T: 'ᛏ', U: 'ᚢ',
    V: 'ᚹ', W: 'ᚹ', X: 'ᚲᛊ', Z: 'ᛉ', 'Þ': 'ᚦ', ' ': '᛫'
  };
  const toRunes = (t) => t.toUpperCase().replace(/TH/g, 'Þ').split('').map((c) => RUNES[c] ?? (/[0-9&'.-]/.test(c) ? c : '')).join('');
  function setupEngrave(el) {
    if (!once(el)) return;
    const input = el.querySelector('[data-in]');
    const out = el.querySelector('[data-out]');
    const count = el.querySelector('[data-count]');
    const chips = [...el.querySelectorAll('[data-letter].np-chip')];
    let letter = 'norse';
    const draw = () => {
      const raw = input.value || ' ';
      out.textContent = letter === 'rune' ? toRunes(raw) : raw;
      out.dataset.letter = letter;
      if (count) count.textContent = input.value.length;
    };
    input?.addEventListener('input', draw);
    chips.forEach((c) => c.addEventListener('click', () => {
      letter = c.dataset.letter;
      pick(chips, c);
      draw();
      if (!reduced()) {
        out.classList.remove('is-pop');
        void out.offsetWidth;
        out.classList.add('is-pop');
      }
    }));
    draw();
  }

  /* ---------- tabs ---------- */
  function setupTabs(el) {
    if (!once(el)) return;
    const tabs = [...el.querySelectorAll('[role="tab"]')];
    const show = (t, focus) => {
      tabs.forEach((x) => {
        const on = x === t;
        x.setAttribute('aria-selected', String(on));
        x.tabIndex = on ? 0 : -1;
        const p = el.querySelector(`#${x.getAttribute('aria-controls')}`);
        if (p) p.hidden = !on;
      });
      if (focus) t.focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => show(t));
      t.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') show(tabs[(i + 1) % tabs.length], true);
        if (e.key === 'ArrowLeft') show(tabs[(i - 1 + tabs.length) % tabs.length], true);
      });
    });
  }

  /* ---------- gift finder ---------- */
  function setupGift(el) {
    if (!once(el)) return;
    const occ = [...el.querySelectorAll('[data-occasion]')];
    const bud = [...el.querySelectorAll('[data-min]')];
    let min = 0;
    let max = 0;
    const apply = () => {
      el.querySelectorAll('.np-gift__panel').forEach((p) => {
        let shown = 0;
        p.querySelectorAll('li[data-price]').forEach((li) => {
          const price = Number(li.dataset.price);
          const ok = price >= min && (!max || price <= max);
          li.hidden = !ok;
          if (ok) shown++;
        });
        const none = p.querySelector('.np-gift__none');
        if (none) none.hidden = shown > 0 || !p.querySelector('li[data-price]');
      });
    };
    occ.forEach((b) => b.addEventListener('click', () => {
      pick(occ, b);
      el.querySelectorAll('.np-gift__panel').forEach((p) => (p.hidden = p.dataset.panel !== b.dataset.occasion));
    }));
    bud.forEach((b) => b.addEventListener('click', () => {
      pick(bud, b);
      min = Number(b.dataset.min) || 0;
      max = Number(b.dataset.max) || 0;
      apply();
    }));
  }

  /* ---------- journal: search and topics ---------- */
  function setupJournal(el) {
    if (!once(el)) return;
    const section = el.closest('.np');
    const grid = section?.querySelector('[data-np-journal-grid]');
    if (!grid) return;
    const input = el.querySelector('[data-j-search]');
    const chips = [...el.querySelectorAll('[data-tag]')];
    let tag = '';
    const apply = () => {
      const words = norm(input?.value).split(/\s+/).filter(Boolean);
      let shown = 0;
      grid.querySelectorAll('.np-jcard').forEach((c) => {
        const hay = `${c.dataset.title} ${c.dataset.tags}`;
        const ok = (!tag || c.dataset.tags.split('|').includes(tag)) && words.every((w) => hay.includes(stem(w)));
        c.hidden = !ok;
        if (ok) shown++;
      });
      const none = section.querySelector('.np-jgrid__none');
      if (none) none.hidden = shown > 0 || !grid.querySelector('.np-jcard');
    };
    input?.addEventListener('input', apply);
    chips.forEach((c) => c.addEventListener('click', () => {
      tag = c.dataset.tag;
      pick(chips, c);
      apply();
    }));
  }

  const init = (root) => {
    watch(root);
    root.querySelectorAll('[data-np-suggest]').forEach(setupSuggest);
    root.querySelectorAll('[data-np-gallery]').forEach(setupGallery);
    root.querySelectorAll('[data-np-engrave]').forEach(setupEngrave);
    root.querySelectorAll('[data-np-tabs]').forEach(setupTabs);
    root.querySelectorAll('[data-np-gift]').forEach(setupGift);
    root.querySelectorAll('[data-np-journal]').forEach(setupJournal);
  };
  init(document);
  // sections added or changed in the theme editor
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
